/**
 * Story 057 — Park-cell drill-down affordance (AC24).
 *
 * Renders the site name as an underlined `<button>` so Tab reaches it
 * and Enter/Space activate the drill-down. Click opens the Story 054
 * Site detail modal in-place over the Turbines list — the Turbines
 * list page mounts `<EditSiteDialog>` at its root and keys it on the
 * `?site=<uuid>` URL param.
 *
 * UX brief §5.2 — underline-on-rest in `text.secondary`, primary on
 * hover/focus; `:focus-visible` ring on `primary.main`.
 *
 * SB6-G2 Bug-7 fix: the pre-fix `textDecorationColor` referenced the
 * CSS variable `var(--mui-palette-text-secondary)` which doesn't
 * exist in this app — `packages/ui/src/theme/index.ts` builds the
 * theme via plain `createTheme()` (not `extendTheme`), so MUI's CSS
 * variables are not emitted. With the variable undefined, browsers
 * fall back to `currentColor` (the parent `text.primary`) for the
 * underline, which on a near-black row of text is visually
 * indistinguishable from no underline at all. Swapping to the sx
 * function form resolves the colours from the JS palette directly.
 */
import { Box } from "@mui/material";
import type { MouseEvent, ReactElement } from "react";

export interface ParkCellProps {
  siteName: string;
  onOpen: () => void;
}

export function ParkCell({ siteName, onOpen }: ParkCellProps): ReactElement {
  function handleClick(e: MouseEvent<HTMLButtonElement>): void {
    // Stop the cell-click bubbling so the row's own onCellClick (if any)
    // doesn't double-fire and open the edit-turbine modal instead.
    e.stopPropagation();
    onOpen();
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={(theme) => ({
        background: "none",
        border: "none",
        padding: 0,
        color: theme.palette.text.primary,
        cursor: "pointer",
        textAlign: "inherit",
        fontFamily: "inherit",
        fontSize: "inherit",
        textDecorationLine: "underline",
        textDecorationStyle: "solid",
        textDecorationThickness: "1px",
        textUnderlineOffset: "3px",
        // R0627-07 — match the Model link (`TypeCell`): a subtle
        // `text.secondary` underline at rest that brightens to
        // `primary.main` on hover/focus, with the text staying primary
        // (no blue text). The JS-palette `sx` function form is the only
        // one that works here — see the file header. `TypeCell` and the
        // other four link cells carried the broken `var(--mui-palette-*)`
        // strings until the design-system refactor; they now match this.
        textDecorationColor: theme.palette.text.secondary,
        "&:hover, &:focus-visible": {
          textDecorationColor: theme.palette.primary.main,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
          borderRadius: "2px",
        },
      })}
    >
      {siteName}
    </Box>
  );
}
